import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {FormComponent} from '../../../../shared/widgets/form';
import {FormInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components/dialog';

import {AdjustmentForm} from '../../forms/panel-accounts';
import {
    CashAccountDetailedReportModel,
    HttpStatusCodesEnum, IAccountCashBalancesResultItem,
    PaymentsService, ReportingNetService, ServerResponseData,
    SingleAdjustmentInputModel,
    SingleAdjustmentResponseInterface,
    TradingAccountDetailedReportModel, TradingAccountModel
} from '@spa/api';
import {
    AdjustmentAmountSelector,
    AdjustmentCategorySelector,
    AdjustmentCommentSelector,
    AdjustmentCurrencySelector
} from 'appAssets/descriptors';
import {AlertsService} from 'appShared/services';
import {Router} from '@angular/router';
import {PanelPaths} from 'appShared/paths';
import {WithdrawalMessages} from 'appAssets/messages';
import {Subscription} from 'rxjs';
import {Helper} from 'appShared/classes';

@Component({
    selector: 'app-adjustment',
    templateUrl: './adjustment.component.html',
    styleUrls: ['./adjustment.component.scss']
})
export class AdjustmentComponent extends DialogComponent<AdjustmentComponent> implements OnInit, FormInterface, OnDestroy {
    private account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;
    private panelPaths;
    private getAccountToDetailsSubscription: Subscription;
    public adjustmentForm: AdjustmentForm;

    constructor(
        @Inject(PaymentsService) private paymentsService: PaymentsService,
        private router: Router,
        private alertsService: AlertsService,
        private reportsService: ReportingNetService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            balanceData: IAccountCashBalancesResultItem,
        },
        public dialogRef: MatDialogRef<AdjustmentComponent>,
    ) {
        super(dialogRef);

        this.config = data.config;
        this.account = data.account;
        this.panelPaths = PanelPaths;
    }

    private getInputModel(): SingleAdjustmentInputModel {
        const currency = this.adjustmentForm.form.value[AdjustmentCurrencySelector];
        const amount = Helper.getAmountValue(this.adjustmentForm.form.value[AdjustmentAmountSelector]);
        const category = this.adjustmentForm.form.value[AdjustmentCategorySelector];
        const comment = this.adjustmentForm.form.value[AdjustmentCommentSelector];

        return new SingleAdjustmentInputModel(
            amount,
            currency,
            category,
            comment,
            this.account.accountId);
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        this.adjustmentForm = new AdjustmentForm();

        this.adjustmentForm.initForm();
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return this.adjustmentForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.paymentsService.singleAdjustment(this.getInputModel())
                        .subscribe(response => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                this.alertsService.addAlert(`Could not create adjustment. ${response.messages.join(', ')}`);
                            } else {
                                const responseData = response.data as SingleAdjustmentResponseInterface;
                                this.router.navigate([
                                    this.panelPaths.root + '/' +
                                    this.panelPaths.children.payments.path + '/' +
                                    this.panelPaths.children.payments.children.edit.path + '/' +
                                    responseData.payment_id
                                ]).finally();

                                this.dialogRef.close(false);
                            }
                        });
                }
                break;
        }
    }

    getAccountDetails(): void {

        const request = this.reportsService.getCrmAccountsCashBalancesMultiCurrency([this.account.accountReferenceId]);
        this.getAccountToDetailsSubscription = request
            .subscribe(this.getAccountRequestSubscriber.bind(this, this.account.accountReferenceId, this.account.currencyCode));

    }

    getAccountRequestSubscriber(
        referenceId: string,
        baseCurrency: string,
        response: ServerResponseData<IAccountCashBalancesResultItem[]>
    ): boolean {
        if ((response.status !== HttpStatusCodesEnum.Ok) || (response.data === null)) {
            this.alertsService.addAlert(WithdrawalMessages.CashBalancesFailed.message);

            return false;
        }
        let model = null;
        if (!response.data) {
            model = [{
                accountReferenceId: referenceId,
                cashBalances: [{
                    currency: baseCurrency,
                    availableCash: 0,
                    netCash: 0
                }]
            } as IAccountCashBalancesResultItem];
        } else {
            model = response.data as IAccountCashBalancesResultItem[];
        }
        this.data.balanceData = model[0];
    }

    ngOnDestroy(): void {
        if (this.getAccountToDetailsSubscription) {
            this.getAccountToDetailsSubscription.unsubscribe();
        }
    }
}

